import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { PhoneNumbersFilterOption } from '../models/phone-number-filter-option';
import { PhoneNumberOrderingOptions, PhoneNumberRegisterOptions } from '../models/phone-number-register-options';
import { GetPhoneNumbers, GetPhoneNumbersVariables } from '../queries/generated/GetPhoneNumbers';
import { getPhoneNumbersQuery } from '../queries/phonenumbers.graphql';

@Injectable({ providedIn: 'root' })
export class PhoneNumberRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getPhoneNumbers(
      endCursor: string | null,
      filters: PhoneNumbersFilterOption[] | null,
      quickFilters: PhoneNumberRegisterOptions | null,
      orderingOptions: PhoneNumberOrderingOptions[] | null
   ): Observable<ExecutionResult<GetPhoneNumbers>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertPhoneNumberFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      filterBuilderFilterExpression.filterOptions.push({
         field: 'IsArchived',
         operation: FilterOperations.EqualTo,
         value: false,
      });
      if (quickFilters) {
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const searchTextNum = searchText.replace(/[^0-9]/g, '');
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };

            if (searchTextNum.length > 0) {
               subExpression.filterOptions.push({
                  field: 'PhoneNumber',
                  operation: FilterOperations.Contains,
                  value: searchTextNum,
               });
            }

            subExpression.filterOptions.push({
               field: 'Account.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'OpenExtension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'ClosedExtension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'Schedule.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'FaxExtension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });

            if (searchTextNum.length > 0 && searchTextNum.length < 10) {
               subExpression.filterOptions.push({
                  field: 'OpenExtension.Number',
                  operation: FilterOperations.Contains,
                  value: searchTextNum,
               });
               subExpression.filterOptions.push({
                  field: 'ClosedExtension.Number',
                  operation: FilterOperations.Contains,
                  value: searchTextNum,
               });
               subExpression.filterOptions.push({
                  field: 'FaxExtension.Number',
                  operation: FilterOperations.Contains,
                  value: searchTextNum,
               });
            }

            const searchLocationWords = searchText.split(/\s+/);

            const locationFieldsToSearch = [
               'Location.Name',
               'Location.HouseNumber',
               'Location.StreetName',
               'Location.DirectionalPrefix',
               'Location.DirectionalPostfix',
               'Location.StreetNameSuffix',
            ];

            const locationsAND: FilterExpression = {
               filterOptions: [],
               logicalOperator: LogicalOperators.And,
               subExpressions: searchLocationWords.map((word) => ({
                  filterOptions: locationFieldsToSearch.map((field) => ({
                     field,
                     operation: FilterOperations.Contains,
                     value: word,
                  })),
                  logicalOperator: LogicalOperators.Or,
               })),
            };

            const orGroup: FilterExpression = {
               filterOptions: [],
               logicalOperator: LogicalOperators.Or,
               subExpressions: [subExpression, locationsAND],
            };

            filterBuilderFilterExpression.logicalOperator = LogicalOperators.Or;
            filterBuilderFilterExpression.subExpressions = [orGroup];
         }
      }
      const variables = this.getPhoneNumbersVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetPhoneNumbers, GetPhoneNumbersVariables>({
         query: getPhoneNumbersQuery,
         variables,
      });
   }

   private convertPhoneNumberFilterOption(option: PhoneNumbersFilterOption): FilterOption {
      return {
         basePath: option.field,
         field: option.field,
         operation: option.operation,
         useAllForNotIn: true,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: PhoneNumbersFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'phoneNumber':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.phoneNumberId;
                 })
               : option.value?.phoneNumberId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }

   private getPhoneNumbersVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetPhoneNumbersVariables {
      const variables: GetPhoneNumbersVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
